import { useEffect, type ReactNode } from "react";
import { X, UserPlus, Trash2 } from "lucide-react";
import { useHiringContacts } from "../modal/HiringContactsContext";
import MultiAssignDropdown from "shared/MultiAssignDropdown";
import type { BulkAssignResult } from "../api/HiringContactsApi";

interface AssigneeLike {
  assignment_id: number;
  employee_id: number;
  employee_code: string;
  name: string;
}

interface Props {
  title: string;
  /** The small info block identifying what's being assigned (name/domain/role etc.) —
   * left to the caller since a contact and a company show different fields. */
  subject: ReactNode;
  assignees: AssigneeLike[];
  onLoadAssignees: () => void;
  onAssign: (employeeIds: number[]) => Promise<BulkAssignResult>;
  onUnassign: (assignmentId: number) => Promise<unknown>;
  onClose: () => void;
}

/** Shared "assign to a TM" flow for both Companies and Contacts — same currently-
 * assigned list (with per-row unassign), same picker excluding already-assigned
 * employees, same add/error/close affordances. Only what's being assigned differs. */
export default function AssignEntityModal({ title, subject, assignees, onLoadAssignees, onAssign, onUnassign, onClose }: Props) {
  const { assignableEmployees } = useHiringContacts();

  // Callers pass a fresh closure each render (e.g. () => loadContactAssignees(contact.id)),
  // so depend on nothing and run once on mount — one modal instance is always scoped
  // to a single entity for its whole lifetime, same as the pre-shared-component version.
  useEffect(() => { onLoadAssignees(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const assignedIds = new Set(assignees.map((a) => a.employee_id));
  const available = assignableEmployees.filter((e) => !assignedIds.has(e.id));

  async function handleConfirm(employeeIds: number[]) {
    const result = await onAssign(employeeIds);
    if (result?.errors?.length) {
      const detail = result.errors
        .map((e) => `${available.find((a) => a.id === e.employee_id)?.name ?? `#${e.employee_id}`}: ${e.detail}`)
        .join("; ");
      throw new Error(`Assigned ${result.assigned.length}, failed for ${result.errors.length} — ${detail}`);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">{title}</h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-surface-muted/60 border border-border/60 rounded-xl p-4 mb-5">
          {subject}
        </div>

        {assignees.length > 0 && (
          <div className="mb-5">
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-2">Currently assigned</label>
            <div className="space-y-1.5">
              {assignees.map((a) => (
                <div key={a.assignment_id} className="flex items-center justify-between bg-surface-muted/60 border border-border/60 rounded-lg px-3 py-1.5">
                  <span className="text-xs text-foreground font-inter">{a.name} ({a.employee_code})</span>
                  <button
                    onClick={() => onUnassign(a.assignment_id)}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <label className="block text-xs font-medium text-muted-foreground font-inter mb-2">Assign to</label>
        {available.length === 0 ? (
          <p className="text-xs text-amber-400 font-inter bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-5">
            {assignableEmployees.length === 0
              ? 'No employees currently have the "LinkedIn Hiring Contacts" permission. Grant it to a role first via Roles Management.'
              : "All eligible employees are already assigned."}
          </p>
        ) : (
          <div className="mb-5">
            <MultiAssignDropdown
              options={available.map((e) => ({ id: e.id, label: `${e.name} (${e.employee_code})` }))}
              onConfirm={handleConfirm}
              placeholder="Select employees…"
              confirmLabel={(n) => (n > 0 ? `Add ${n}` : "Add")}
            />
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
